/**
 * Client-Side Demo Write Guard
 *
 * REQUIREMENTS SUMMARY:
 * - Provides a `blockIfDemo()` check that components call before any mutation.
 * - When in demo mode, shows a warning toast and returns true (= blocked).
 * - When not in demo mode, returns false (= allow the action).
 * - Centralises the user-facing message so it's consistent everywhere.
 *
 * @example
 * const { blockIfDemo } = useDemoGuard();
 *
 * const handleSave = async () => {
 *   if (blockIfDemo()) return;
 *   // proceed with save…
 * };
 */

"use client";

import { useCallback } from 'react';
import { useDemo } from '@/context/DemoContext';
import { useNotification } from '@/components/ui/notification/NotificationProvider';

export function useDemoGuard() {
  const { isDemoMode } = useDemo();
  const { showNotification } = useNotification();

  /**
   * Call before any create / update / delete action.
   * Returns `true` if the action was blocked (demo mode is active).
   */
  const blockIfDemo = useCallback((): boolean => {
    if (isDemoMode) {
      showNotification(
        'warning',
        'Demo Mode',
        'Editing is disabled in demo mode. Exit demo mode to make changes to your own gym.',
        6000,
      );
      return true;
    }
    return false;
  }, [isDemoMode, showNotification]);

  return { isDemoMode, blockIfDemo };
}
